import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { apiFetch, ApiProblem, type StaffMonth, type StaffToday } from '../lib/api.ts';
import { useBusinessNow } from '../lib/business-time.ts';
import { clockStateChip } from '../lib/clock-steps.ts';
import { WEEKDAYS_SHORT, weekdayIndex } from '../lib/date-field.ts';
import { useScreen } from '../lib/layout.ts';
import { useSession } from '../lib/session.tsx';
import { berlinMinute, hoursMinutes, hoursText } from '../lib/staff-display.ts';
import { cn } from '../lib/utils.ts';
import { TIME_TRACKING_PATH } from '../navigation.ts';
import { ForgottenNotice, useClockStamp, useStaffToday } from './clock-card.tsx';
import { Button } from './ui/button.tsx';
import { DayTimeline } from './ui/day-timeline.tsx';
import { Popover } from './ui/popover.tsx';

export const CLOCK_CHIP_TEXTS = {
  dialog: 'Zeiterfassung heute',
  come: 'Kommen',
  pause: 'Pause',
  since: (time: string) => `seit ${time}`,
  left: (time: string) => `gegangen ${time}`,
  head: (day: string) => `Heute · ${day}`,
  ofTarget: (work: string, target: string) => `${work} von ${target}`,
  came: 'Gekommen',
  breaks: 'Pause',
  weekBalance: 'Saldo Woche',
  toTimeTracking: 'Zur Zeiterfassung',
  /** Erster Abruf gescheitert (nicht „kein Mitarbeiter“): ruhiger Chip mit Fragezeichen statt stillem Verschwinden. */
  unknown: 'Zeit ?',
  unknownState: 'Zeiterfassung nicht geladen',
  loadError: 'Die Zeiterfassung konnte nicht geladen werden. Bitte erneut laden.',
  reload: 'Erneut laden',
  /** Zusatz im Namen, wenn gestern „Gehen“ fehlt (das Zeichen am Chip ist nie nur Farbe). */
  forgotten: 'Gehen fehlt',
  /** Name für Hilfsmittel: beginnt mit dem sichtbaren Text (WCAG „Label in Name“), dann der Zustand. */
  name: (visible: string, state: string) => `${visible} – Zeiterfassung: ${state}`,
} as const;

/** „Di 16.02.“ aus dem fachlichen Datum `YYYY-MM-DD` (nur Schreibweise). */
function dayLabel(date: string): string {
  return `${WEEKDAYS_SHORT[weekdayIndex(date)]} ${date.slice(8, 10)}.${date.slice(5, 7)}.`;
}

/**
 * Saldo der laufenden Woche – nur aus dem vorhandenen Monats-Endpunkt (`GET /staff/me/months/{month}`, gleiche Abfrage
 * wie Zeiterfassung › Saldo): die Woche, die den heutigen Tag enthält, mit ihrem Text des Servers. Keine Rechnung.
 */
function useWeekBalance(date: string | undefined, enabled: boolean): string | null {
  const { getToken } = useSession();
  const month = date?.slice(0, 7);
  const query = useQuery({
    queryKey: ['staff', 'month', 'me', month],
    queryFn: () => apiFetch<StaffMonth>(getToken, `/api/v1/staff/me/months/${month}`),
    enabled: enabled && !!month,
    retry: false,
  });
  const week = query.data?.weeks.find((w) => w.days.some((d) => d.date === date));
  return week?.balanceText ? week.balanceText : null;
}

/** Sichtbarer Inhalt des Chips je Zustand (Punkt + Wort, nie Farbe allein; Zeiten nur vom Server). */
function chipLook(data: StaffToday): { tone: 'ready' | 'check' | 'neutral'; main: string; sub: string | null; state: string } {
  const day = data.day!;
  const chip = clockStateChip(data.state, day.entries);
  const sinceTime = (pick: (e: (typeof day.entries)[number]) => boolean) => [...day.entries].reverse().find(pick)?.from ?? null;
  if (data.state === 'present') {
    const since = sinceTime((e) => e.type === 'clock_in');
    return { tone: 'ready', main: hoursMinutes(day.workMinutes), sub: since ? CLOCK_CHIP_TEXTS.since(since) : null, state: chip.text };
  }
  if (data.state === 'on_break') {
    const since = sinceTime((e) => e.type === 'break' && e.to === null);
    return { tone: 'check', main: CLOCK_CHIP_TEXTS.pause, sub: since ? CLOCK_CHIP_TEXTS.since(since) : null, state: chip.text };
  }
  const left = sinceTime((e) => e.type === 'clock_out');
  return left
    ? { tone: 'neutral', main: hoursMinutes(day.workMinutes), sub: CLOCK_CHIP_TEXTS.left(left), state: chip.text }
    : { tone: 'neutral', main: CLOCK_CHIP_TEXTS.come, sub: null, state: chip.text };
}

const DOT = { ready: 'bg-ready', check: 'bg-check-dot', neutral: 'bg-ink-3' } as const;

/**
 * Stempel-Chip der Kopfzeile (Ticket 1.275, [RUDOLF 25.10.2026]): ruhiger Chip mit Punkt und Zeit – da = grüner Punkt,
 * „3:27“ und „seit 07:58“; Pause = Bernstein-Fläche, Punkt, „Pause · seit 12:05“; nicht gekommen = grauer Punkt und
 * „Kommen“. Kein Stempel-Knopf direkt in der Kopfzeile: Klick öffnet ein Popover (am Handy dasselbe als Blatt von unten)
 * mit Tag, Tagesleiste, Gekommen/Pause/Saldo Woche und den Knöpfen des Servers. Stempeln, Rückgängig und Frist kommen aus
 * `useClockStamp` (dieselbe Logik wie die Stempel-Karte). Ohne Mitarbeiter-Stammsatz oder mit Sperre (Austritt) kein Chip.
 * `compact` (Kopfzeile Handy): nur Punkt und Zeit, Trefferfläche 44 px. Fehlt gestern „Gehen“, trägt der Chip ein
 * Bernstein-„!“ und das Fenster oben den Hinweis mit „Nachtragen“ (wie früher die Stempel-Karte der Startseite).
 */
export function ClockChip({ compact = false }: { compact?: boolean }) {
  const today = useStaffToday();
  const data = today.data;
  const day = data?.day ?? null;
  const clock = useClockStamp(day?.date);
  const [open, setOpen] = useState(false);
  const phone = useScreen() === 'phone';
  const { now } = useBusinessNow();
  const weekBalance = useWeekBalance(day?.date, open && !!day);
  // Still verschwinden nur, wenn der Server „kein Mitarbeiter-Eintrag“ (`day === null`) oder eine Sperre bestätigt hat.
  // Scheitert der erste Abruf, bleibt ein ruhiger Chip „Zeit ?“ mit Fehlersatz und „Erneut laden“ (Zweit-Review 1.275).
  if (!data && today.error) {
    const name = CLOCK_CHIP_TEXTS.name(CLOCK_CHIP_TEXTS.unknown, CLOCK_CHIP_TEXTS.unknownState);
    return (
      <Popover
        open={open}
        onOpenChange={setOpen}
        label={CLOCK_CHIP_TEXTS.dialog}
        align={phone ? 'start' : 'end'}
        className="w-popover-w"
        trigger={(props) => (
          <button
            type="button"
            {...props}
            aria-label={name}
            data-testid="stempel-chip"
            data-state="error"
            className={cn(
              'relative inline-flex h-11 flex-none cursor-pointer items-center gap-space-2 rounded-full border border-line px-space-3 text-row whitespace-nowrap text-ink tabular-nums transition-colors duration-150 hover:bg-hover',
              compact && 'h-11 min-w-11 justify-center px-space-2',
            )}
          >
            <span aria-hidden data-state-dot="" className={cn('size-space-2 flex-none rounded-full', DOT.neutral)} />
            <span className="font-medium">{CLOCK_CHIP_TEXTS.unknown}</span>
          </button>
        )}
      >
        <div data-testid="stempel-popover" className="flex min-w-0 flex-col gap-space-3">
          <p role="alert" data-testid="stempel-chip-fehler" className="m-0 text-row text-clarify [overflow-wrap:anywhere]">
            {today.error instanceof ApiProblem && today.error.detail ? today.error.detail : CLOCK_CHIP_TEXTS.loadError}
          </p>
          <Button variant="secondary" className="w-fit" disabled={today.isFetching} onClick={() => void today.refetch()}>
            {CLOCK_CHIP_TEXTS.reload}
          </Button>
        </div>
      </Popover>
    );
  }
  if (!data || !day || data.blocked) return clock.toast;
  const look = chipLook(data);
  const firstIn = day.entries.find((e) => e.type === 'clock_in')?.from ?? null;
  const visible = compact || !look.sub ? look.main : `${look.main} ${look.sub}`;
  const name = CLOCK_CHIP_TEXTS.name(
    visible,
    `${look.state}${data.state === 'present' ? `, heute ${hoursText(day.workMinutes)}` : ''}${data.forgotten ? `, ${CLOCK_CHIP_TEXTS.forgotten}` : ''}`,
  );
  return (
    <>
      <Popover
        open={open}
        onOpenChange={setOpen}
        label={CLOCK_CHIP_TEXTS.dialog}
        align={phone ? 'start' : 'end'}
        className="w-popover-w"
        trigger={(props) => (
          <button
            type="button"
            {...props}
            aria-label={name}
            data-testid="stempel-chip"
            data-state={data.state}
            data-forgotten={data.forgotten ? '' : undefined}
            className={cn(
              'relative inline-flex h-11 flex-none cursor-pointer items-center gap-space-2 rounded-full border border-line px-space-3 text-row whitespace-nowrap text-ink tabular-nums',
              look.tone === 'check' ? 'bg-check-bg hover:bg-check-bg' : 'hover:bg-hover',
              compact && 'h-11 min-w-11 justify-center px-space-2',
            )}
          >
            <span aria-hidden data-state-dot="" className={cn('size-space-2 flex-none rounded-full', DOT[look.tone])} />
            {data.forgotten ? (
              // Gestern fehlt „Gehen“: Bernstein-Zeichen „!“ an der Ecke (Form + Farbe; im Namen „Gehen fehlt“), sichtbar ohne Öffnen.
              <span
                aria-hidden
                data-testid="stempel-chip-gehen-fehlt"
                className="absolute -top-space-1 -right-space-1 grid size-space-4 place-items-center rounded-full bg-check-dot text-2xs leading-none font-semibold text-ink ring-2 ring-surface"
              >
                !
              </span>
            ) : null}
            <span className="font-medium">{look.main}</span>
            {look.sub && !compact ? (
              // Leerzeichen im Text: der Name für Hilfsmittel beginnt mit genau diesem sichtbaren Text („Label in Name“).
              <>
                {' '}
                <span className="text-ink-3">{look.sub}</span>
              </>
            ) : null}
          </button>
        )}
      >
        <div data-testid="stempel-popover" className="flex min-w-0 flex-col gap-space-3">
          {data.forgotten ? <ForgottenNotice forgotten={data.forgotten} onNavigate={() => setOpen(false)} /> : null}
          <div className="flex flex-wrap items-baseline justify-between gap-x-space-3">
            <p className="m-0 text-row font-medium text-ink">{CLOCK_CHIP_TEXTS.head(dayLabel(day.date))}</p>
            <p data-testid="stempel-popover-ist" className="m-0 type-meta text-ink-2 tabular-nums">
              {day.targetMinutes > 0 ? CLOCK_CHIP_TEXTS.ofTarget(hoursMinutes(day.workMinutes), hoursMinutes(day.targetMinutes)) : hoursText(day.workMinutes)}
            </p>
          </div>
          {day.entries.length > 0 ? (
            <DayTimeline
              testId="stempel-popover-zeitleiste"
              entries={day.entries}
              now={berlinMinute(now)}
              label={`Tag als Zeitleiste: ${day.entries.map((e) => e.text).join(', ')}, jetzt`}
              className="rounded-tile bg-tile px-space-3 py-space-2"
            />
          ) : null}
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-space-4 gap-y-space-1 text-row">
            {firstIn ? (
              <>
                <dt className="text-ink-3">{CLOCK_CHIP_TEXTS.came}</dt>
                <dd className="m-0 text-right tabular-nums">{firstIn}</dd>
              </>
            ) : null}
            {day.breakMinutes > 0 ? (
              <>
                <dt className="text-ink-3">{CLOCK_CHIP_TEXTS.breaks}</dt>
                <dd className="m-0 text-right tabular-nums">{hoursText(day.breakMinutes)}</dd>
              </>
            ) : null}
            {weekBalance ? (
              <>
                <dt className="text-ink-3">{CLOCK_CHIP_TEXTS.weekBalance}</dt>
                <dd data-testid="stempel-popover-saldo" className="m-0 text-right tabular-nums">
                  {weekBalance}
                </dd>
              </>
            ) : null}
          </dl>
          {clock.errorText ? (
            <p role="alert" data-testid="stempel-fehler" className="m-0 text-row text-clarify [overflow-wrap:anywhere]">
              {clock.errorText}
            </p>
          ) : null}
          {data.buttons.length > 0 ? (
            <div className="flex flex-wrap gap-space-2">
              {data.buttons.map((b, i) => (
                <Button
                  key={b.kind}
                  variant={i === 0 ? 'primary' : 'secondary'}
                  data-testid={i === 0 ? 'stempel-chip-hauptknopf' : 'stempel-chip-zweitknopf'}
                  disabled={clock.busy}
                  onClick={() => clock.press(b.kind)}
                >
                  {b.label}
                </Button>
              ))}
            </div>
          ) : null}
          <Link to={TIME_TRACKING_PATH} onClick={() => setOpen(false)} className="type-meta w-fit text-link underline-offset-2 hover:underline pointer-coarse:min-h-11">
            {CLOCK_CHIP_TEXTS.toTimeTracking}
          </Link>
        </div>
      </Popover>
      {clock.toast}
    </>
  );
}
